<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  
  data() {
    return {
      challan: null,
      challanItems: [],
      finishedItems: [],
      rawItems: [],
      preparedItems: [],
      wastageItems: [],
      remark: '',
      loading: false,
      submitting: false,
      errors: {},
    };
  },

  computed: {
    canSubmit() {
      return this.preparedItems.some(i => i.item_id && parseFloat(i.quantity) >= 0);
    },
  },

  methods: {
    async open(challanId) {
      this.resetForm();
      this.loading = true;
      try {
        const [challanRes, finishedRes, rawRes] = await Promise.all([
          axios.get(`/inventory/delivery-challans/${challanId}`),
          axios.get('/items/dropdown?inventory_type=finish'),
          axios.get('/items/dropdown?inventory_type=raw'),
        ]);
        this.challan = challanRes.data.data;
        this.challanItems = this.challan.challan_items || [];
        this.finishedItems = finishedRes.data.data || [];
        this.rawItems = rawRes.data.data || [];
        this.addPrepared();
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    addPrepared() {
      this.preparedItems.push({ item_id: '', quantity: '' });
    },
    removePrepared(index) {
      this.preparedItems.splice(index, 1);
    },
    addWastage() {
      this.wastageItems.push({ item_id: '', quantity: '', remark: '' });
    },
    removeWastage(index) {
      this.wastageItems.splice(index, 1);
    },

    validate() {
      this.errors = {};
      const validItems = this.preparedItems.filter(i => i.item_id && parseFloat(i.quantity) >= 0);
      if (validItems.length === 0) {
        this.errors.items = 'At least one prepared item is required.';
      }
      this.preparedItems.forEach((item, idx) => {
        if (item.item_id && (item.quantity === '' || item.quantity === null || item.quantity === undefined || parseFloat(item.quantity) < 0)) {
          this.errors[`prep_${idx}_qty`] = 'Enter quantity.';
        }
        if (!item.item_id && item.quantity) {
          this.errors[`prep_${idx}_item`] = 'Select an item.';
        }
      });
      this.wastageItems.forEach((w, idx) => {
        if (!w.item_id) this.errors[`wastage_${idx}_item`] = 'Select an item.';
        if (!w.quantity || parseFloat(w.quantity) <= 0) this.errors[`wastage_${idx}_qty`] = 'Enter quantity.';
        if (!w.remark || !w.remark.trim()) this.errors[`wastage_${idx}_remark`] = 'Remark is required.';
      });
      return Object.keys(this.errors).length === 0;
    },

    async submit() {
      if (!this.validate()) return;
      const result = await this.$swal({
        title: 'Submit Production?',
        text: 'Are you sure you want to submit this production entry?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Submit',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#2563eb',
      });
      if (!result.isConfirmed) return;

      this.submitting = true;
      try {
        const items = this.preparedItems
          .filter(i => i.item_id && parseFloat(i.quantity) >= 0)
          .map(i => ({ item_id: parseInt(i.item_id), quantity: parseFloat(i.quantity) }));
        const wastages = this.wastageItems
          .filter(w => w.item_id && parseFloat(w.quantity) > 0)
          .map(w => ({ item_id: parseInt(w.item_id), quantity: parseFloat(w.quantity), remark: w.remark.trim() }));

        const payload = { items };
        if (wastages.length > 0) payload.wastages = wastages;
        if (this.remark.trim()) payload.remark = this.remark.trim();

        const response = await axios.post(`/inventory/productions/delivery-challan/${this.challan.id}`, payload);
        this.$swalToast(response.data.message || 'Production completed successfully.');
        this.$emit('updated');
        this.closeModal();
      } catch (error) {
        if (error.response?.data?.errors) this.errors = error.response.data.errors;
        this.$swalToast(error.response?.data?.message || error.message, 'error');
      } finally {
        this.submitting = false;
      }
    },

    closeModal() {
      const modalEl = document.getElementById('production_modal');
      const modal = bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();
    },

    resetForm() {
      this.challan = null;
      this.challanItems = [];
      this.finishedItems = [];
      this.rawItems = [];
      this.preparedItems = [];
      this.wastageItems = [];
      this.remark = '';
      this.errors = {};
      this.loading = false;
      this.submitting = false;
    },
  },
};
</script>

<template>
  <div class="modal fade" id="production_modal" tabindex="-1" data-bs-backdrop="static">
    <div class="modal-dialog modal-fullscreen" style="margin:0;">
      <div class="modal-content border-0 h-100" style="border-radius:0;">

        <div class="pm-main">

          <!-- Topbar -->
          <div class="pm-topbar">
            <div>
              <div class="pm-page-title">Create Production</div>
              <div class="pm-page-sub" v-if="challan">
                {{ challan.challan_number }} &bull; {{ challan.recipient_details?.name || '-' }}
              </div>
              <div class="pm-page-sub" v-else>Record finished goods and wastage from delivery challan</div>
            </div>
            <button type="button" class="pm-close-btn" data-bs-dismiss="modal" :disabled="submitting">
              <i class="ti ti-x"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="pm-body">

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border spinner-border-sm text-primary me-2"></div>
              <span style="color:#94a3b8;">Loading challan data...</span>
            </div>

            <template v-else>

              <!-- DC Reference Card -->
              <div class="pm-card" v-if="challanItems.length">
                <div class="pm-card-head">
                  <div class="pm-card-icon" style="background:#eff6ff;"><i class="ti ti-file-text" style="color:#3b82f6;"></i></div>
                  <span class="pm-card-title">DC Items (Reference)</span>
                </div>
                <div class="pm-card-body">
                  <div class="d-flex flex-wrap gap-2">
                    <span v-for="ci in challanItems" :key="ci.id" class="pm-ref-badge">
                      {{ ci.name || ci.item?.name }}
                      <span class="pm-ref-qty">Qty: {{ ci.quantity }}</span>
                      <span v-if="ci.unit || ci.item?.unit" class="pm-ref-unit">({{ ci.unit || ci.item?.unit }})</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Side by Side Cards -->
              <div class="row g-3">

                <!-- LEFT: What We Prepared -->
                <div class="col-lg-6">
                  <div class="pm-card">
                    <div class="pm-card-head">
                      <div class="pm-card-icon" style="background:#f0fdf4;"><i class="ti ti-package" style="color:#16a34a;"></i></div>
                      <span class="pm-card-title">What We Prepared</span>
                      <button type="button" class="pm-add-btn ms-auto" @click="addPrepared">
                        <i class="ti ti-plus"></i> Add Item
                      </button>
                    </div>
                    <div class="pm-card-body">
                      <div class="pm-err-msg mb-2" v-if="errors.items">
                        <i class="ti ti-alert-circle"></i>{{ Array.isArray(errors.items) ? errors.items[0] : errors.items }}
                      </div>

                      <div v-for="(item, index) in preparedItems" :key="'prep-' + index" class="pm-item-card">
                        <div class="pm-item-num">{{ index + 1 }}</div>
                        <div class="row g-2 align-items-end">
                          <div class="col">
                            <label class="pm-label">Finished Item</label>
                            <vue-select
                              :options="finishedItems"
                              label="name"
                              :reduce="i => i.id"
                              v-model="item.item_id"
                              placeholder="Select finished item"
                              :class="{ 'is-invalid': errors[`prep_${index}_item`] || errors[`items.${index}.item_id`] }"
                            />
                            <div class="pm-err-msg" v-if="errors[`prep_${index}_item`] || errors[`items.${index}.item_id`]">
                              <i class="ti ti-alert-circle"></i>{{ errors[`prep_${index}_item`] || (Array.isArray(errors[`items.${index}.item_id`]) ? errors[`items.${index}.item_id`][0] : errors[`items.${index}.item_id`]) }}
                            </div>
                          </div>
                          <div style="width:100px;">
                            <label class="pm-label">Qty</label>
                            <input type="number" class="pm-input text-center" v-model="item.quantity" placeholder="0" min="0.01" step="0.01"
                              :class="{ 'err': errors[`prep_${index}_qty`] || errors[`items.${index}.quantity`] }" />
                            <div class="pm-err-msg" v-if="errors[`prep_${index}_qty`] || errors[`items.${index}.quantity`]">
                              <i class="ti ti-alert-circle"></i>{{ errors[`prep_${index}_qty`] || (Array.isArray(errors[`items.${index}.quantity`]) ? errors[`items.${index}.quantity`][0] : errors[`items.${index}.quantity`]) }}
                            </div>
                          </div>
                          <div style="width:40px;" class="d-flex align-items-end">
                            <button type="button" class="pm-remove-btn" @click="removePrepared(index)" v-if="preparedItems.length > 1">
                              <i class="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div v-if="!preparedItems.length" class="pm-empty">
                        <i class="ti ti-package-off"></i>
                        <p>No items added yet.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- RIGHT: Wastage -->
                <div class="col-lg-6">
                  <div class="pm-card">
                    <div class="pm-card-head">
                      <div class="pm-card-icon" style="background:#fff0f3;"><i class="ti ti-recycle" style="color:#f43f5e;"></i></div>
                      <span class="pm-card-title">Wastage During Production</span>
                      <button type="button" class="pm-add-btn pm-add-btn-danger ms-auto" @click="addWastage">
                        <i class="ti ti-plus"></i> Add Wastage
                      </button>
                    </div>
                    <div class="pm-card-body">

                      <div v-for="(w, index) in wastageItems" :key="'waste-' + index" class="pm-item-card">
                        <div class="pm-item-num" style="background:#f43f5e;">{{ index + 1 }}</div>
                        <div class="row g-2 align-items-end">
                          <div class="col">
                            <label class="pm-label">Raw Material</label>
                            <vue-select
                              :options="rawItems"
                              label="name"
                              :reduce="i => i.id"
                              v-model="w.item_id"
                              placeholder="Select raw material"
                              :class="{ 'is-invalid': errors[`wastage_${index}_item`] || errors[`wastages.${index}.item_id`] }"
                            />
                            <div class="pm-err-msg" v-if="errors[`wastage_${index}_item`] || errors[`wastages.${index}.item_id`]">
                              <i class="ti ti-alert-circle"></i>{{ errors[`wastage_${index}_item`] || (Array.isArray(errors[`wastages.${index}.item_id`]) ? errors[`wastages.${index}.item_id`][0] : errors[`wastages.${index}.item_id`]) }}
                            </div>
                          </div>
                          <div style="width:90px;">
                            <label class="pm-label">Qty</label>
                            <input type="number" class="pm-input text-center" v-model="w.quantity" placeholder="0" min="0.01" step="0.01"
                              :class="{ 'err': errors[`wastage_${index}_qty`] || errors[`wastages.${index}.quantity`] }" />
                          </div>
                          <div style="width:40px;" class="d-flex align-items-end">
                            <button type="button" class="pm-remove-btn" @click="removeWastage(index)">
                              <i class="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                        <div class="mt-2">
                          <label class="pm-label">Remark <span style="color:#f43f5e;">*</span></label>
                          <input type="text" class="pm-input" v-model="w.remark" placeholder="Reason for wastage..."
                            maxlength="255" :class="{ 'err': errors[`wastage_${index}_remark`] || errors[`wastages.${index}.remark`] }" />
                          <div class="pm-err-msg" v-if="errors[`wastage_${index}_remark`] || errors[`wastages.${index}.remark`]">
                            <i class="ti ti-alert-circle"></i>{{ errors[`wastage_${index}_remark`] || (Array.isArray(errors[`wastages.${index}.remark`]) ? errors[`wastages.${index}.remark`][0] : errors[`wastages.${index}.remark`]) }}
                          </div>
                        </div>
                      </div>

                      <div v-if="!wastageItems.length" class="pm-empty">
                        <i class="ti ti-recycle"></i>
                        <p>No wastage recorded.</p>
                        <small>Click <strong>+ Add Wastage</strong> to record wastage items.</small>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Overall Remark Card -->
              <div class="pm-card">
                <div class="pm-card-head">
                  <div class="pm-card-icon" style="background:#faf5ff;"><i class="ti ti-notes" style="color:#8b5cf6;"></i></div>
                  <span class="pm-card-title">Additional Notes</span>
                </div>
                <div class="pm-card-body">
                  <label class="pm-label">Overall Remark <span style="color:#94a3b8;font-weight:400;">(optional)</span></label>
                  <textarea class="pm-textarea" v-model="remark" rows="3" placeholder="Any additional notes about this production..." maxlength="1000"></textarea>
                </div>
              </div>

            </template>
          </div>

          <!-- Footer -->
          <div class="pm-footer">
            <button type="button" class="pm-btn pm-btn-ghost me-auto" data-bs-dismiss="modal" :disabled="submitting">
              Cancel
            </button>
            <button type="button" class="pm-btn pm-btn-primary" @click="submit" :disabled="submitting || loading || !canSubmit">
              <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Submitting...</span>
              <span v-else><i class="ti ti-check me-1"></i>Submit Production</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Main layout */
.pm-main { display:flex; flex-direction:column; height:100%; background:#f1f5f9; }

/* Topbar */
.pm-topbar {
  background:#fff; border-bottom:1px solid #e2e8f0;
  padding:12px 22px; display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
}
.pm-page-title { font-size:14px; font-weight:700; color:#0f172a; }
.pm-page-sub { font-size:11px; color:#94a3b8; }
.pm-close-btn {
  width:30px; height:30px; border-radius:7px;
  background:#f1f5f9; border:1px solid #ef4444;
  display:flex; align-items:center; justify-content:center;
  cursor:pointer; font-size:13px; color:#ef4444; transition:all .15s;
}
.pm-close-btn:hover { background:#fee2e2; border-color:#fca5a5; }

/* Body */
.pm-body {
  flex:1; overflow-y:auto; padding:18px 22px;
  scrollbar-width:thin; scrollbar-color:#cbd5e1 transparent;
}
.pm-body::-webkit-scrollbar { width:4px; }
.pm-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:10px; }

/* Cards */
.pm-card {
  background:#fff; border:1px solid #e2e8f0;
  border-radius:12px; margin-bottom:14px; overflow:visible;
}
.pm-card-head {
  display:flex; align-items:center; gap:9px;
  padding:10px 16px; background:#f8fafc; border-bottom:1px solid #e8edf3;
}
.pm-card-icon {
  width:24px; height:24px; border-radius:6px;
  display:flex; align-items:center; justify-content:center;
  font-size:12px; flex-shrink:0;
}
.pm-card-title { font-size:12px; font-weight:700; color:#0f172a; letter-spacing:.1px; }
.pm-card-body { padding:14px 16px; }

/* Labels & Inputs */
.pm-label { font-size:11px; font-weight:600; color:#475569; margin-bottom:4px; display:block; }
.pm-input, .pm-textarea {
  width:100%; border:1.5px solid #e2e8f0; border-radius:8px;
  padding:0 11px; font-size:12.5px; color:#1e293b; background:#fff; outline:none;
  transition:border-color .15s, box-shadow .15s;
}
.pm-input { height:35px; }
.pm-textarea { padding:8px 11px; resize:vertical; }
.pm-input:focus, .pm-textarea:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
.pm-input.err { border-color:#f43f5e; box-shadow:0 0 0 3px rgba(244,63,94,.1); }

/* Error */
.pm-err-msg { font-size:10.5px; color:#f43f5e; margin-top:3px; display:flex; align-items:center; gap:3px; }

/* Item cards */
.pm-item-card {
  background:#fff; border:1.5px solid #e8edf3; border-radius:10px;
  padding:14px 14px 10px; margin-bottom:10px; position:relative;
  transition:border-color .15s, box-shadow .15s;
}
.pm-item-card:hover { border-color:#bfdbfe; box-shadow:0 2px 10px rgba(59,130,246,.07); }
.pm-item-num {
  position:absolute; top:-9px; left:14px;
  width:19px; height:19px; background:#16a34a; color:#fff;
  border-radius:50%; font-size:9px; font-weight:700;
  display:flex; align-items:center; justify-content:center;
}

/* Add button */
.pm-add-btn {
  display:inline-flex; align-items:center; gap:5px;
  padding:5px 12px; background:#eff6ff; border:1.5px dashed #93c5fd;
  border-radius:7px; color:#2563eb; font-size:11px; font-weight:600;
  cursor:pointer; transition:all .15s;
}
.pm-add-btn:hover { background:#dbeafe; border-color:#3b82f6; }
.pm-add-btn-danger { background:#fff0f3; border-color:#fecdd3; color:#f43f5e; }
.pm-add-btn-danger:hover { background:#ffe4e6; border-color:#f43f5e; }

/* Remove button */
.pm-remove-btn {
  width:30px; height:30px; border-radius:7px;
  background:#fff0f3; border:1.5px solid #fecdd3;
  color:#f43f5e; font-size:13px; cursor:pointer;
  display:flex; align-items:center; justify-content:center; transition:all .15s;
}
.pm-remove-btn:hover { background:#f43f5e; border-color:#f43f5e; color:#fff; }

/* Reference badges */
.pm-ref-badge {
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 12px; background:#f8fafc; border:1px solid #e2e8f0;
  border-radius:8px; font-size:12px; color:#1e293b; font-weight:500;
}
.pm-ref-qty { color:#3b82f6; font-weight:600; }
.pm-ref-unit { color:#94a3b8; font-size:11px; }

/* Empty state */
.pm-empty { text-align:center; padding:28px; color:#94a3b8; }
.pm-empty i { font-size:28px; display:block; margin-bottom:6px; opacity:.4; }
.pm-empty p { margin:0 0 4px; font-size:13px; }
.pm-empty small { font-size:11px; }

/* Footer */
.pm-footer {
  background:#fff; border-top:1px solid #e2e8f0;
  padding:11px 22px; display:flex; align-items:center; gap:8px; flex-shrink:0;
}

/* Buttons */
.pm-btn {
  display:inline-flex; align-items:center; gap:5px;
  padding:7px 15px; border-radius:8px; font-size:12px;
  font-weight:600; cursor:pointer; border:1.5px solid transparent;
  transition:all .15s; white-space:nowrap;
}
.pm-btn-primary {
  background:linear-gradient(135deg,#2563eb,#3b82f6);
  color:#fff; border-color:#2563eb; box-shadow:0 2px 8px rgba(37,99,235,.25);
}
.pm-btn-primary:hover { background:linear-gradient(135deg,#1d4ed8,#2563eb); }
.pm-btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.pm-btn-ghost { background:#f8fafc; color:#475569; border-color:#e2e8f0; }
.pm-btn-ghost:hover { background:#f1f5f9; }

/* vue-select overrides */
:deep(.vs__dropdown-toggle) {
  border:1.5px solid #e2e8f0 !important; border-radius:8px !important;
  min-height:35px !important; padding:0 8px !important;
  transition:border-color .15s, box-shadow .15s !important;
}
:deep(.vs__dropdown-toggle:focus-within) {
  border-color:#3b82f6 !important; box-shadow:0 0 0 3px rgba(59,130,246,.1) !important;
}
:deep(.vs__search) { font-size:12.5px !important; color:#1e293b !important; }
:deep(.vs__selected) { font-size:12px !important; color:#1e293b !important; }
:deep(.vs__dropdown-menu) { font-size:12.5px !important; max-height:200px; }
:deep(.vs__dropdown-option) { padding:7px 12px; }
:deep(.vs__dropdown-option--highlight) { background:#eff6ff !important; color:#2563eb !important; }

/* Responsive */
@media (max-width:768px) {
  .pm-body { padding:12px; }
  .pm-topbar { padding:10px 14px; }
  .pm-footer { padding:10px 14px; flex-wrap:wrap; }
}
</style>